/**
 * OAuth scope lists and authorization URLs for the ad platforms.
 *
 * The scope catalogs live in `lib/services/platformAccess/scopes/` and what
 * each feature needs lives in its registry; this file re-exports the lists
 * for existing imports (the frontend builds connect URLs from it) and turns a
 * set of capabilities into an authorization URL. Never compare scopes here or
 * anywhere else: ask `platformAccess` whether an account can do something.
 */

import { assertNever } from "@newform/constants";
import type { Platform } from "@newform-monorepo/db";
import { z } from "zod";
import {
  type AccessCapability,
  capabilitiesForPlatform,
  oauthScopesFor,
  READ_CAPABILITIES,
} from "../services/platformAccess/registry";
import { GOOGLE_SCOPES } from "../services/platformAccess/scopes/google";
import {
  META_SCOPES,
  READ_ONLY_META_SCOPES,
} from "../services/platformAccess/scopes/meta";
import { SNAPCHAT_SCOPES } from "../services/platformAccess/scopes/snapchat";
import {
  MINIMAL_TIKTOK_SCOPES,
  READ_ONLY_TIKTOK_SCOPES,
} from "../services/platformAccess/scopes/tiktok";

export { GOOGLE_SCOPES } from "../services/platformAccess/scopes/google";
export {
  META_BRANDED_CONTENT_SCOPES,
  META_CREATOR_MARKETPLACE_SCOPES,
  META_SCOPES,
  MINIMAL_META_SCOPES,
  READ_ONLY_META_SCOPES,
} from "../services/platformAccess/scopes/meta";
export { SNAPCHAT_SCOPES } from "../services/platformAccess/scopes/snapchat";
export {
  MINIMAL_TIKTOK_SCOPES,
  READ_ONLY_TIKTOK_SCOPES,
  TIKTOK_ADVANCED_CREATIVE_SCOPES,
  TIKTOK_SCOPES,
} from "../services/platformAccess/scopes/tiktok";

export type OAuthAccessLevel = "read" | "manage";

/** The baseline scope list a connection at this access level is expected to hold. */
export function getExpectedScopesForPlatform(
  platform: Platform,
  accessLevel: OAuthAccessLevel = "manage",
): string[] {
  switch (platform) {
    case "META":
      return accessLevel === "read" ? READ_ONLY_META_SCOPES : META_SCOPES;
    case "GOOGLE":
      return GOOGLE_SCOPES;
    case "TIKTOK":
      return accessLevel === "read"
        ? READ_ONLY_TIKTOK_SCOPES
        : MINIMAL_TIKTOK_SCOPES;
    case "SNAPCHAT":
      return SNAPCHAT_SCOPES;
    case "APPLOVIN":
      // AppLovin authenticates its Reporting API with a static report key
      // entered by hand, so there is no authorization flow and no scopes.
      throw new Error("AppLovin does not use OAuth");
    default:
      return assertNever(platform, "Unsupported OAuth platform");
  }
}

/**
 * The scopes an authorization request asks for: everything the access
 * level's capabilities need, plus whatever `capabilities` adds. A manage
 * connection covers every capability the platform offers, so a reconnect
 * for one missing feature still asks for the whole baseline and a narrower
 * grant never silently replaces a wider one.
 */
export function requestedScopesFor(
  platform: Platform,
  accessLevel: OAuthAccessLevel,
  capabilities: readonly AccessCapability[] = [],
): string[] {
  const baseline =
    accessLevel === "read"
      ? READ_CAPABILITIES
      : capabilitiesForPlatform(platform);
  return oauthScopesFor(platform, [...baseline, ...capabilities]);
}

/**
 * Generate OAuth URL for a platform.
 *
 * `rerequest` is for a reconnect that exists to add permissions. Meta never
 * shows a permission again once the user declined it unless the dialog is
 * opened with `auth_type=rerequest`, so without it a "reconnect to grant X"
 * button silently grants the same set as before.
 */
export function generateOAuthUrl(
  platform: Platform,
  params: {
    slug: string;
    /** The in-app path to land on afterwards, from `oauthReturnPath`. */
    returnTo: string;
    accountId: string;
    /**
     * Fresh for each connect click. The browser keeps a copy in a cookie, and
     * the callback refuses a state whose nonce doesn't match it.
     */
    nonce: string;
    accessLevel?: OAuthAccessLevel;
    capabilities?: readonly AccessCapability[];
    rerequest?: boolean;
  },
): string {
  const {
    slug,
    returnTo,
    accountId,
    nonce,
    accessLevel = "manage",
    capabilities = [],
    rerequest = false,
  } = params;
  const statePayload = JSON.stringify([slug, returnTo, accountId, nonce]);
  const encodedState = encodeURIComponent(statePayload);

  switch (platform) {
    case "META": {
      const scopes = requestedScopesFor("META", accessLevel, capabilities);
      const rerequestParam = rerequest ? "&auth_type=rerequest" : "";
      return `https://www.facebook.com/v22.0/dialog/oauth?client_id=${process.env.NEXT_PUBLIC_META_CLIENT_ID}&redirect_uri=${process.env.NEXT_PUBLIC_META_REDIRECT_URI}&state=${encodedState}&response_type=code&scope=${scopes.join("%20")}${rerequestParam}`;
    }

    case "GOOGLE":
      return `https://accounts.google.com/o/oauth2/v2/auth?access_type=offline&include_granted_scopes=true&response_type=code&client_id=${process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID}&redirect_uri=${process.env.NEXT_PUBLIC_GOOGLE_REDIRECT_URI}&state=${encodedState}&scope=${encodeURIComponent(GOOGLE_SCOPES.join(" "))}&prompt=consent`;

    case "TIKTOK": {
      // Without a scope parameter TikTok offers every permission the app is
      // approved for, which is what a manage connection wants.
      const baseUrl = `https://business-api.tiktok.com/portal/auth?app_id=7368322056863105040&state=${encodedState}&redirect_uri=${process.env.NEXT_PUBLIC_TIKTOK_REDIRECT_URI}`;
      if (accessLevel !== "read") {
        return baseUrl;
      }
      const scopes = requestedScopesFor("TIKTOK", "read", capabilities);
      return `${baseUrl}&scope=${encodeURIComponent(scopes.join(","))}`;
    }

    case "SNAPCHAT":
      // NEXT_PUBLIC_ prefix required — this URL is generated client-side in the
      // browser (the server-only SNAPCHAT_CLIENT_ID isn't visible here). Guard
      // the interpolation so an unset var renders "" instead of a literal
      // "undefined" client_id. The backend token exchange resolves the client
      // id via getSnapchatClientId (SNAPCHAT_CLIENT_ID ?? NEXT_PUBLIC_…), so the
      // deployment must keep NEXT_PUBLIC_SNAPCHAT_CLIENT_ID equal to it or the
      // code exchange fails (code issued to a different client).
      return `https://accounts.snapchat.com/login/oauth2/authorize?client_id=${process.env.NEXT_PUBLIC_SNAPCHAT_CLIENT_ID ?? ""}&redirect_uri=${encodeURIComponent(process.env.NEXT_PUBLIC_SNAPCHAT_REDIRECT_URI ?? "")}&response_type=code&scope=${SNAPCHAT_SCOPES.join("%20")}&state=${encodedState}`;

    case "APPLOVIN":
      // No authorization URL: the report key is pasted in rather than granted.
      throw new Error("AppLovin does not use OAuth");

    default:
      return assertNever(platform, "Unsupported OAuth platform");
  }
}

const OAuthStateSchema = z.tuple([
  z.string(),
  z.string(),
  z.string(),
  z.string(),
]);

export interface OAuthState {
  slug: string;
  /**
   * Where the person asked to come back to. Unvalidated: it rode through the
   * platform in the query string, so check it is a same-origin path before
   * redirecting to it.
   */
  returnTo: string;
  accountId: string;
  /**
   * The connect click's nonce, for the callback to compare with the browser's
   * cookie.
   */
  nonce: string;
}

function decodeOrNull(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Reads the `state` {@link generateOAuthUrl} sent, as the platform hands it
 * back. Accepts it decoded once (what `URLSearchParams` gives) or still
 * encoded. Null when it isn't ours.
 */
export function parseOAuthState(state: string | null): OAuthState | null {
  if (!state) return null;
  for (const candidate of [state, decodeOrNull(state)]) {
    if (candidate === null) continue;
    try {
      const parsed = OAuthStateSchema.safeParse(JSON.parse(candidate));
      if (parsed.success) {
        const [slug, returnTo, accountId, nonce] = parsed.data;
        return { slug, returnTo, accountId, nonce };
      }
    } catch {
      // Not JSON in this form; try the next.
    }
  }
  return null;
}
